import { useState } from "react";
import { XIcon } from "@phosphor-icons/react";

import { REQUEST_STATUS } from "../constants/status";
import { VACATION_REQUESTS_STRINGS } from "../constants/strings";

import { ColaboraButton, Heading } from "components/v2";
import { Toast } from "components/toast";

import { useCurrentCompany } from "hooks/useCurrentCompany";
import { useRequestDetail } from "../hooks";

type RejectRequestPropsT = {
  requestIds: string[];
  onClose: () => void;
};

const MAX_CHARS = 150;

const RejectRequest = ({ requestIds, onClose }: RejectRequestPropsT) => {
  const [reason, setReason] = useState("");

  const companyInfo = useCurrentCompany();

  const { rejectRequest } = useRequestDetail({
    requestId: requestIds[0], // hook requires one id; mutation accepts many
    colaboraInstanceId: companyInfo?.instanceId,
  });

  const handleConfirm = async () => {
    try {
      const { failed } = await rejectRequest.mutateAsync({
        requestsToUpdate: requestIds.map((requestId) => ({
          requestId,
          newStatus: REQUEST_STATUS.REJECTED,
          comment: reason.trim() || undefined,
        })),
      });

      if (failed.length > 0) {
        Toast("warning", VACATION_REQUESTS_STRINGS.UPDATE_DATA_ERROR_TITLE);
        rejectRequest.reset();
      }
    } catch (error) {
      Toast(
        "error",
        VACATION_REQUESTS_STRINGS.UPDATE_DATA_ERROR_TITLE,
        error.message,
      );
    }
  };

  return (
    <div className="relative flex flex-col h-full">
      <div className="flex items-center justify-between px-4 pt-3 shrink-0">
        <Heading level={2}>
          {VACATION_REQUESTS_STRINGS.TEXT_TOOLTIP_REJECT}
        </Heading>
        <button className="cursor-pointer p-2 shrink-0" onClick={onClose}>
          <XIcon size={20} />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto flex flex-col gap-2 px-4 pb-4">
        {/* Description */}
        <p className="leading-relaxed">
          Puedes{" "}
          <span className="font-medium">indicar el motivo del rechazo.</span>{" "}
          Este comentario será visible para el colaborador y le ayudará a
          entender la decisión.
        </p>

        {/* Textarea field */}
        <div className="flex flex-col gap-0.5">
          <label>Motivo del rechazo</label>
          <div className="rounded-xl border border-gray-300 px-3 pt-3 pb-2 focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-500 transition-all">
            <textarea
              value={reason}
              onChange={(e) => {
                if (e.target.value.length <= MAX_CHARS)
                  setReason(e.target.value);
              }}
              placeholder="Escribe el motivo (Opcional)"
              rows={4}
              className="w-full resize-none placeholder-gray-400 outline-none bg-transparent"
            />
            <div className="flex justify-end">
              <span className="text-xs">
                {reason.length}/{MAX_CHARS}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-2 p-4 border-t">
        <ColaboraButton
          label={VACATION_REQUESTS_STRINGS.TEXT_TOOLTIP_REJECT}
          errorLabel={VACATION_REQUESTS_STRINGS.DETAIL_REJECT_ERROR_LABEL}
          successLabel={VACATION_REQUESTS_STRINGS.DETAIL_REJECT_SUCCESS_LABEL}
          variant="secondary"
          isLoading={rejectRequest.isPending}
          isSuccess={rejectRequest.isSuccess}
          isError={rejectRequest.isError}
          onClick={handleConfirm}
          onSuccessAnimationEnd={onClose}
        />
      </div>
    </div>
  );
};

export default RejectRequest;
